import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import ChevronDown from 'dibs-icons/exports/ChevronDown.ts';

import dibsCss from 'dibs-css';

import { trackPdpAccordionExpand } from '../tracking/pdpItemModuleTracking.ts';

import styles from './PdpRespDetailAccordions.module.css';

type Props = {
    summary: ReactNode;
    children: ReactNode;
    defaultOpen?: boolean;
    dataTn?: string;
    contentDataTn?: string;
    forceOpen?: boolean;
    onForceOpenHandled?: () => void;
    isLast?: boolean;
    trackingTrigger?: string;
};

export const PdpRespDetailAccordionItem: FC<Props> = ({
    summary,
    children,
    defaultOpen = false,
    dataTn,
    contentDataTn,
    forceOpen = false,
    onForceOpenHandled,
    isLast = false,
    trackingTrigger,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [isHovered, setIsHovered] = useState(false);
    const skipNextToggleTrackingRef = useRef(false);

    useEffect(() => {
        if (!forceOpen) {
            return;
        }

        skipNextToggleTrackingRef.current = true;
        setIsOpen(true);
        onForceOpenHandled?.();
    }, [forceOpen, onForceOpenHandled]);

    return (
        <li>
            <details
                open={isOpen}
                onToggle={event => {
                    const nextOpen = event.currentTarget.open;
                    if (skipNextToggleTrackingRef.current) {
                        skipNextToggleTrackingRef.current = false;
                        setIsOpen(nextOpen);
                        return;
                    }

                    setIsOpen(nextOpen);
                    if (nextOpen && !isOpen && trackingTrigger) {
                        trackPdpAccordionExpand(trackingTrigger);
                    }
                }}
                className={classnames(
                    styles.details,
                    dibsCss.borderT,
                    dibsCss.borderTSolid,
                    dibsCss.borderTDolphin,
                    {
                        [dibsCss.borderB]: isLast,
                        [dibsCss.borderBSolid]: isLast,
                        [dibsCss.borderBDolphin]: isLast,
                    }
                )}
                data-tn={dataTn}
            >
                <summary
                    className={classnames(
                        styles.listNone,
                        dibsCss.sassyFontBodyHeaderMediumHeavy,
                        dibsCss.pySmall,
                        dibsCss.cursorPointer,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyBetween,
                        dibsCss.gapSmall,
                        {
                            [dibsCss.bgMoonstone]: isHovered,
                        }
                    )}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <span>{summary}</span>
                    <span
                        className={classnames(dibsCss.flex, dibsCss.flexShrink0, styles.chevron, {
                            [styles.chevronOpen]: isOpen,
                        })}
                        aria-hidden
                    >
                        <ChevronDown variation="24" />
                    </span>
                </summary>
                <div className={dibsCss.pbMedium} data-tn={contentDataTn}>
                    {children}
                </div>
            </details>
        </li>
    );
};
